import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  useTheme,
  useMediaQuery,
  CircularProgress,
  Pagination,
  Avatar,
  Badge,
  TextField,
  InputAdornment,
  Breadcrumbs,
  Link as MUILink,
  Divider,
} from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import {
  setSearchValue,
  fetchBuyerConnections,
  updateConnectionStatus,
  clearError,
} from "../redux/slices/buyerNetworkSlice";
import SearchIcon from "@mui/icons-material/Search";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import { toast } from "react-toastify";
import SellerChatModal from "../Components/SellerChatModal";
import { socket } from "../socket";
import { useNavigate, Link } from "react-router-dom";
import PageTitle from "../Components/PageTitle";

export default function SellerRequest() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const itemsPerPage = 10;
  const { buyers, searchValue, loading, error, totalPages } = useSelector(
    (state) => state.buyerNetwork,
  );

  const [chatOpen, setChatOpen] = useState(false);
  const [activeBuyer, setActiveBuyer] = useState(null);
  const { user } = useSelector((state) => state.user);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [currentMode, setCurrentMode] = useState("buyer");

  useEffect(() => {
    dispatch(fetchBuyerConnections({ page, limit: itemsPerPage }));
  }, [dispatch, page]);

  useEffect(() => {
    socket.on("onlineUsers", (users) => {
      setOnlineUsers(users);
    });

    return () => socket.off("onlineUsers");
  }, []);

  const handleStatusUpdate = async (connectionId, status, buyerData) => {
    try {
      const result = await dispatch(
        updateConnectionStatus({ connectionId, status }),
      );
      if (updateConnectionStatus.fulfilled.match(result)) {
        // Status update successful
        toast.success(`Request ${status.toLowerCase()} successfully`);
        dispatch(fetchBuyerConnections({ page, limit: itemsPerPage }));
      } else {
        toast.error(result.payload || "Failed to update status");
      }
    } catch (err) {
      toast.error(err?.message || "Failed to update connection status");
    }
  };

  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  const searchedBuyers = buyers?.filter(
    (b) =>
      b.name?.toLowerCase().includes(searchValue?.toLowerCase()) ||
      b.city?.toLowerCase().includes(searchValue?.toLowerCase()) ||
      b.email?.toLowerCase().includes(searchValue?.toLowerCase()) ||
      b.company?.toLowerCase().includes(searchValue?.toLowerCase()),
  );
  const filteredBuyers = searchedBuyers?.filter(
    (b) => b.status === "Pending" || b.status === "Rejected",
  );

  const handlePageChange = (event, value) => {
    setPage(value);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const getStatusStyle = (status) => {
    switch (status) {
      case "Pending":
        return { bg: "#fef3c7", color: "#92400e", label: "Pending" };
      case "Rejected":
        return { bg: "#fee2e2", color: "#991b1b", label: "Rejected" };
      default:
        return { bg: "#e5e7eb", color: "#374151", label: status };
    }
  };

  return (
    <>
    <PageTitle title="Seller Requests - BizBridge" 
    description="Manage your seller requests and connections"
    keywords="seller requests, buyer connections, seller management"
    url="/buyer-dashboard/seller-requests"
    />
      <Box
        sx={{
          width: "100%",
          minHeight: "100vh",
          background: "#f8fafc",
          py: { xs: 1, sm: 2 },
        }}
      >
        {/* Breadcrumb */}
        <Breadcrumbs
          aria-label="breadcrumb"
          sx={{
            width: "fit-content",
            backgroundColor: "#fff",
            ml: { xs: 1, sm: 2.9 },
            padding: "13px",
            borderRadius: "12px",
            boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
          }}
        >
          <MUILink
            component={Link}
            to="/buyer-dashboard"
            sx={{ color: "inherit", textDecoration: "none" }}
          >
            Dashboard
          </MUILink>
          <Typography sx={{ color: "#0b4064", fontWeight: "bold" }}>
            Seller Requests
          </Typography>
        </Breadcrumbs>

        <Divider sx={{ my: 2 }} />
        <Box sx={{ maxWidth: 1350, mx: "auto", px: { xs: 2, sm: 3 } }}>
          <Grid container spacing={2} alignItems="center" mb={3}>
            {/* Title */}
            <Grid item xs={12} md={6}>
              <Typography fontWeight={700} fontSize={22}>
                Seller Requests
              </Typography>
            </Grid>

            {/* Search (Right aligned) */}
            <Grid
              item
              xs={12}
              md={6}
              sx={{
                display: "flex",
                justifyContent: { xs: "flex-start", md: "flex-end" },
              }}
            >
              <TextField
                placeholder="Search buyer name, city, email & company..."
                variant="outlined"
                size="small"
                value={searchValue}
                onChange={(e) => dispatch(setSearchValue(e.target.value))}
                sx={{
                  width: { xs: "100%", sm: "300px" },
                  background: "#fff",
                  borderRadius: 2,
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: "#8b99a8" }} />
                    </InputAdornment>
                  ),
                  endAdornment: searchValue && (
                    <InputAdornment position="end">
                      <Button
                        onClick={() => dispatch(setSearchValue(""))}
                        sx={{ minWidth: 0, color: "#8b99a8", fontSize: 14 }}
                      >
                        ✕
                      </Button>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>

          {/* Loading State */}
          {loading && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {/* Buyers Grid */}
          {!loading && (
            <Grid
              container
              spacing={isMobile ? 3 : 4}
              justifyContent="flex-start"
              sx={{ px: { xs: 1, sm: 0 } }}
            >
              {filteredBuyers?.length === 0 ? (
                <Box sx={{ width: "100%", textAlign: "center", py: 6 }}>
                  <Typography variant="body1" color="text.secondary">
                    No pending or rejected requests found.
                  </Typography>
                </Box>
              ) : (
                filteredBuyers.map((buyer) => (
                  <Grid key={buyer._id} item xs={12} sm={12} md={6} lg={4}>
                    <Card
                      elevation={0}
                      sx={{
                        borderRadius: 3,
                        boxShadow: "0px 6px 18px rgba(0,0,0,0.06)",
                      }}
                    >
                      <CardContent sx={{ p: 3, position: "relative" }}>
                        {/* STATUS BADGE */}
                        {(() => {
                          const statusStyle = getStatusStyle(buyer.status);
                          return (
                            <Box
                              sx={{
                                position: "absolute",
                                top: 12,
                                right: 12,
                                px: 1.5,
                                py: 0.5,
                                borderRadius: 2,
                                fontSize: 12,
                                fontWeight: 700,
                                backgroundColor: statusStyle.bg,
                                color: statusStyle.color,
                              }}
                            >
                              {statusStyle.label}
                            </Box>
                          );
                        })()}

                        {/* Avatar with Online Status */}
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            mb: 2,
                          }}
                        >
                          <Badge
                            overlap="circular"
                            anchorOrigin={{
                              vertical: "bottom",
                              horizontal: "right",
                            }}
                            variant="dot"
                            sx={{
                              "& .MuiBadge-badge": {
                                backgroundColor: onlineUsers.includes(
                                  buyer.buyerId,
                                )
                                  ? "#22c55e"
                                  : "#9ca3af",
                                width: 12,
                                height: 12,
                                borderRadius: "50%",
                                border: "2px solid white",
                              },
                            }}
                          >
                            <Avatar
                              alt={buyer.name}
                              src={
                                buyer.profileImage ||
                                "https://via.placeholder.com/60"
                              }
                              sx={{
                                width: 60,
                                height: 60,
                              }}
                            />
                          </Badge>
                        </Box>

                        {/* Name */}
                        <Typography
                          fontWeight={700}
                          fontSize={16}
                          textAlign="center"
                        >
                          {buyer.name}
                        </Typography>

                        {/* Company/City */}
                        <Typography
                          variant="body2"
                          textAlign="center"
                          sx={{ color: "#6b7280", mb: 2 }}
                        >
                          {buyer.company || buyer.city}
                        </Typography>
                        {buyer.status === "Pending" ? (
                          <Box sx={{ display: "flex", gap: 1 }}>
                            <Button
                              fullWidth
                              variant="contained"
                              sx={{
                                bgcolor: "#22c55e",
                                color: "white",
                                fontWeight: 600,
                                boxShadow: "none",
                                "&:hover": { bgcolor: "#16a34a" },
                              }}
                              onClick={() =>
                                handleStatusUpdate(buyer._id, "Accepted", buyer)
                              }
                            >
                              Accept
                            </Button>
                            <Button
                              fullWidth
                              variant="outlined"
                              sx={{
                                color: "#dc2626",
                                borderColor: "#dc2626",
                                fontWeight: 600,
                                "&:hover": {
                                  borderColor: "#b91c1c",
                                  backgroundColor: "#fef2f2",
                                },
                              }}
                              onClick={() =>
                                handleStatusUpdate(buyer._id, "Rejected", buyer)
                              }
                            >
                              Reject
                            </Button>
                          </Box>
                        ) : buyer.status === "Rejected" ? (
                          <Button
                            fullWidth
                            variant="contained"
                            sx={{
                              bgcolor: "#0b4064",
                              color: "white",
                              fontWeight: 600,
                              boxShadow: "none",
                              "&:hover": { bgcolor: "#0b4064" },
                            }}
                            onClick={() =>
                              handleStatusUpdate(buyer._id, "Pending", buyer)
                            }
                          >
                            Restore Request
                          </Button>
                        ) : null}
                      </CardContent>
                    </Card>
                  </Grid>
                ))
              )}
            </Grid>
          )}

          {/* Pagination */}
          {!loading && filteredBuyers?.length > 1 && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                mt: 6,
                pb: 4,
              }}
            >
              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                color="primary"
                size={isMobile ? "small" : "medium"}
                shape="rounded"
                showFirstButton
                showLastButton
              />
            </Box>
          )}
        </Box>

        {/* Chat Modal */}
        <SellerChatModal
          open={chatOpen}
          onClose={() => setChatOpen(false)}
          seller={activeBuyer}
          currentUser={user?._id}
          currentMode={currentMode}
          onlineUsers={onlineUsers}
        />
      </Box>
    </>
  );
}
